import { jsPDF } from "jspdf";

export interface SPTJElektronikData {
  name: string;
  position: string;
  companyName: string;
  smuNo: string;
  flightNo: string;
  route: string;
  electronicGoods: string;
  date: string;
}

const generateSPTJElektronikPDF = async (data: SPTJElektronikData) => {
  const doc: any = new jsPDF("p", "mm", "a4");

  // Load logos
  const logoLion = await import("@/app/ui/assets/images/logo-lion-air.png");
  const logoBatik = await import("@/app/ui/assets/images/logo-batik-air.png");
  const logoSuperjet = await import("@/app/ui/assets/images/logo-super-air-jet-short.png");
  const logoWings = await import("@/app/ui/assets/images/logo-wings-air.png");
  const logoLionParcel = await import("@/app/ui/assets/images/logo-lion-parcel.png");

  // Header Logos
  const yLogo = 10;
  let xLogo = 10;
  const logoWidth = 45;
  const logoHeight = 10;
  const gap = 5;

  // Adjust logo sizes/aspect ratios if needed, for now using uniform size
  // Lion Air
  doc.addImage(logoLion.default, "PNG", xLogo, yLogo, logoWidth, logoHeight, "lion", "FAST");
  xLogo += logoWidth + gap;
  
  // Batik Air
  doc.addImage(logoBatik.default, "PNG", xLogo, yLogo, logoWidth, logoHeight, "batik", "FAST");
  xLogo += logoWidth + gap;
  
  // Super Air Jet
  doc.addImage(logoSuperjet.default, "PNG", xLogo, yLogo, logoWidth, logoHeight, "superjet", "FAST");
  xLogo += logoWidth + gap;
  
  // Wings Air
  doc.addImage(logoWings.default, "PNG", xLogo, yLogo, logoWidth, logoHeight, "wings", "FAST");
  
  // Lion Parcel Logo centered below
  // Center of A4 (210mm) is 105mm. Logo width approx 50mm. x = 105 - 25 = 80.
  doc.addImage(logoLionParcel.default, "PNG", 80, yLogo + 15, 55, 12, "lionparcel", "FAST");

  // Title
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(11);
  doc.text("SURAT PERNYATAAN PENGANGKUTAN LITHIUM ION BATTERY", 105, 45, { align: "center" });

  // Body
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(10);
  let y = 55;
  const leftMargin = 10;
  const labelX = 10;
  const colonX = 75;
  const valueX = 78;
  const maxWidth = 190;

  const introText = "Sehubungan dengan adanya kerjasama keagenan kargo PT. Lion Mentari, bersama ini saya bertindak dan atas nama perusahaan kami:";
  const splitIntro = doc.splitTextToSize(introText, maxWidth);
  doc.text(splitIntro, leftMargin, y);
  y += splitIntro.length * 5 + 5;

  // Personal Details
  const details = [
    { label: "Nama", value: "" },
    { label: "Jabatan", value: "Operasional" },
    { label: "Nama Perusahaan", value: "Lion Parcel" }
  ];

  details.forEach(detail => {
    doc.text(detail.label, labelX, y);
    doc.text(":", colonX, y);
    doc.text(detail.value, valueX, y);
    y += 6;
  });
  y += 4;

  // Shipment Details Header
  doc.setFont("Montserrat", "bold");
  const shipmentHeader = "Akan melakukan pengiriman Electronic Goods menggunakan penerbangan Lion Air, dengan details sebagai berikut:";
  const splitShipmentHeader = doc.splitTextToSize(shipmentHeader, maxWidth);
  doc.text(splitShipmentHeader, leftMargin, y);
  y += splitShipmentHeader.length * 5 + 5;
  doc.setFont("Montserrat", "normal");

  // Shipment Details
  const shipmentDetails = [
    { label: "SMU/AWB No", value: data.smuNo },
    { label: "Flight No", value: data.flightNo },
    { label: "Rute Penerbangan", value: data.route },
    { label: "Jenis Electronic Goods", value: data.electronicGoods }
  ];

  shipmentDetails.forEach(detail => {
    doc.text(detail.label, labelX, y);
    doc.text(":", colonX, y);
    doc.text(detail.value, valueX, y);
    y += 6;
  });
  y += 4;

  // Statements
  doc.setFont("Montserrat", "bold");
  doc.text("Bahwa dengan ini kami menyatakan:", leftMargin, y);
  doc.setFont("Montserrat", "normal");
  y += 10;

  const points = [
    "Bahwa kami tunduk sepenuhnya kepada aturan dan ketentuan yang berlaku terkait pengiriman barang/kargo (Electronic Goods) yang telah ditetapkan PT. Lion Mentari.",
    "Kelengkapan Dokumen Pengangkutan Lithium Ion Battery, serta Standar kemasan sudah sesuai dengan Aturan dan Ketentuan yang berlaku",
    "Batasan kapasitas:", // Special handling for sub-points
    "Bilamana terjadi kehilangan, kerusakan, incident serta accident yang timbul baik isi,/maupun kemasan atas kiriman kargo tersebut merupakan tanggung jawab kami sepenuhnya.",
    "Bahwa semua ganti rugi atas kehilangan, kerusakan maupun keterlambatan semua pengiriman baik kiriman milik sendiri ataupun kiriman milik sub-agent kami serta pihak lainnya yang mempergunakan Surat Muatan Udara/Airwaybil (SMU/AWB) yang kami miliki merupakan tanggung jawab kami sepenuhnya.",
    "Bahwa Surat ini dibuat dengan sebenar - benarnya, serta Salinan surat ini berlaku untuk dipergunakan sebagaimana mestinya."
  ];

  points.forEach((point, i) => {
    const index = i + 1;
    doc.text(`${index}.`, leftMargin, y);
    
    if (index === 3) {
       doc.text(point, leftMargin + 5, y);
       y += 3;
       doc.text("Lithium ION Cell <= 20 Wh setara dengan Kapasitas Battery Max 5.000 mAh", leftMargin + 5, y + 1);
       y += 4;
       doc.text("Lithium Battery <= 100 Wh setara dengan Kapasitas Battery Max 9.000 mAh", leftMargin + 5, y + 1);
       y += 6; 
    } else {
       const splitPoint = doc.splitTextToSize(point, maxWidth - 5);
       doc.text(splitPoint, leftMargin + 5, y);
       y += splitPoint.length * 4 + 1.5;
    }
  });
  
  y += 10;
  doc.text("Demikian surat pernyataan ini kami buat dengan sebenar-benar nya.", leftMargin + 5, y);
  y += 10;

  // Date
  doc.text(data.date, 196, y + 3, { align: "right" });
  y += 10;

  // Signatures
  const leftSigCenter = 33;
  const rightSigCenter = 175;

  doc.text("Diketahui oleh:", leftSigCenter, y, { align: "center" });
  doc.text("Dibuat oleh:", rightSigCenter, y, { align: "center" });
  y += 5;
  doc.text("Petugas Airlines", leftSigCenter, y, { align: "center" });
  doc.text("Pengirim", rightSigCenter, y, { align: "center" });

  y += 20;
  doc.text("(.....................................................)", leftSigCenter, y, { align: "center" });
  doc.text("(.....................................................)", rightSigCenter, y, { align: "center" });
  y += 5;
  doc.text("Nama & No ID", leftSigCenter, y, { align: "center" });
  doc.text("Nama jelas", rightSigCenter, y, { align: "center" });

  y += 15;
  // Footer Note
  doc.setFont("Montserrat", "bolditalic");
  doc.text("Nb: File surat ini disimpan di Station Origin", leftMargin, y);
  y += 5;
  doc.text("(*)Dicoret salah satu saja", leftMargin, y);

  doc.setProperties({
    title: `SPTJ_Elektronik_${data.smuNo}.pdf`
  });

  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

export default generateSPTJElektronikPDF;
